// @ts-nocheck
import { MOCK } from "@/state/app-store";
import { UI } from "@/components/ui";
import { Icon } from "@/components/icons";
import { api } from "@/api/index";

const { $, $$, fmtNum, fmtMoney, fmtPercent, toast, confirmModal, hasPerm, statusBadge, trendArrow, statCard, channelChip, capabilityBadge, pageHeader, emptyState, primaryBtn, ghostBtn, stepIndicator, BUSINESS_COLORS, chartTooltip, chartXAxis, chartYAxis, chartLineSeries, chartBarSeries } = UI;

declare const echarts: any;
declare const Pages: Record<string, any>;
declare const PageHooks: { mounted?: ((path: string) => void) | null };
declare function navigate(path: string): void;
declare function getCurrentPath(): string;
declare function getCurrentPathWithQuery(): string;
declare function renderRoute(): void;

if (typeof window !== "undefined") {
  (window as any).Pages = (window as any).Pages || {};
}

const Pages = (window as any).Pages;

// ============================================================
// 包体创建成功页 - 包体信息 + 继承凭证说明 + 下一步指引
// 关键信息：Ripple ID / Region / Platform / App ID / Store URL / Game ID
// ============================================================

window.Pages = window.Pages || {};
window.__newPackageResult = window.__newPackageResult || null;

Pages.packageSuccess = function (packageId) {
  let result = window.__newPackageResult;
  if (!result || result.package?.id !== packageId) {
    try {
      const raw = sessionStorage.getItem(`easyshare:newPackageResult:${packageId}`);
      if (raw) {
        const parsed = JSON.parse(raw);
        result = { ...parsed, createdAt: new Date(parsed.createdAt) };
        window.__newPackageResult = result;
      }
    } catch (e) {
      console.warn("Failed to restore new package result", e);
    }
  }

  let pkg = result?.package || MOCK.PACKAGES.find(p => p.id === packageId);
  let game = result?.game || MOCK.GAMES.find(g => g.id === pkg?.game_id);
  if (!pkg || !game) {
    return `
      ${UI.pageHeader({ title: "创建结果不可见" })}
      <div class="card p-10 text-center max-w-2xl mx-auto">
        <div class="w-14 h-14 mx-auto mb-3 rounded-full bg-warn-50 border border-warn-200 flex items-center justify-center">
          ${Icon.info({ size: 24, color: "#B45309" })}
        </div>
        <h3 class="text-base font-semibold text-ink-900">该包体创建结果页已失效</h3>
        <p class="text-sm text-ink-500 mt-2 max-w-md mx-auto">未找到刚创建的包体信息。你可以回到包体管理或游戏详情页查看当前包体列表。</p>
        <div class="mt-5 flex items-center justify-center gap-2">
          ${UI.ghostBtn("返回包体管理", { onclick: "navigate('/dev/packages')" })}
          ${UI.primaryBtn("返回游戏管理", { onclick: "navigate('/dev/games')" })}
        </div>
      </div>
    `;
  }

  const createdAt = result?.createdAt || new Date();
  const createdStr = createdAt.toLocaleString("zh-CN", { hour12: false });
  const creator = result?.creator || MOCK.ROLES[MOCK.SESSION.currentRole]?.user || "当前用户";
  const iconHtml = typeof game.icon === "string" && game.icon.startsWith("data:image")
    ? `<img src="${game.icon}" class="w-7 h-7 rounded-md object-cover border border-ink-200" alt="${game.name}" />`
    : `<span class="text-lg">${game.icon}</span>`;
  const appIdLabel = packageAppIdLabel(pkg.platform);
  const packageDisplayName = pkg.name || `${game.code}-${pkg.region}-${pkg.platform}`;
  const regionText = `${pkg.region_name} (${pkg.region})`;
  const platformIcon = pkg.platform === "iOS"
    ? Icon.apple({ size: 14, color: "#1E3A8A" })
    : (pkg.platform === "Google Play" || pkg.platform === "Android")
      ? Icon.play({ size: 14, color: "#1E3A8A" })
      : Icon.monitor({ size: 14, color: "#1E3A8A" });

  return `
    <div class="max-w-6xl mx-auto space-y-5">
      <div class="card overflow-hidden shadow-sm">
        <div class="h-1 bg-gradient-to-r from-ok-500 via-brand-500 to-brand-900"></div>
        <div class="p-6 bg-white">
          <div class="flex items-start justify-between gap-5">
            <div class="flex items-start gap-4 min-w-0">
              <div class="w-12 h-12 rounded-2xl bg-ink-50 border border-ink-200 flex items-center justify-center flex-shrink-0 shadow-sm overflow-hidden">
                ${iconHtml}
              </div>
              <div class="min-w-0">
                <div class="flex items-center gap-2 mb-2">
                  <span class="badge bg-ok-50 text-ok-700">创建成功</span>
                  <span class="badge bg-brand-50 text-brand-700">Draft</span>
                </div>
                <h1 class="text-2xl font-semibold tracking-tight text-ink-900">${packageDisplayName}</h1>
                <p class="text-sm text-ink-500 mt-1">所属游戏：${game.name}。包体已创建完成，可继续接入 SDK 或配置分享策略。</p>
              </div>
            </div>
            <button onclick="downloadPackageResult('${pkg.id}')" class="px-3 py-2 text-xs rounded-lg bg-brand-900 text-white hover:bg-brand-800 font-semibold flex items-center gap-1.5 flex-shrink-0 shadow-sm">
              ${Icon.download({ size: 13, color: "#FFFFFF" })}
              下载信息
            </button>
          </div>

          <div class="grid grid-cols-4 gap-3 mt-5">
            <div class="rounded-xl border border-ink-200 bg-ink-50/70 p-3">
              <div class="text-[11px] font-semibold text-ink-400 mb-1">所属游戏</div>
              <div class="flex items-center gap-2 min-w-0">
                ${iconHtml}
                <div class="min-w-0">
                  <div class="text-sm font-semibold text-ink-900 truncate">${game.name}</div>
                  <div class="text-[11px] font-mono text-ink-500 truncate">${game.code}</div>
                </div>
              </div>
            </div>
            <div class="rounded-xl border border-ink-200 bg-white p-3">
              <div class="text-[11px] font-semibold text-ink-400 mb-1">Platform</div>
              <div class="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-900">${platformIcon}${pkg.platform}</div>
            </div>
            <div class="rounded-xl border border-ink-200 bg-white p-3">
              <div class="text-[11px] font-semibold text-ink-400 mb-1">Region</div>
              <div class="text-sm font-semibold text-ink-900 truncate">${regionText}</div>
            </div>
            <div class="rounded-xl border border-brand-200 bg-brand-50/70 p-3">
              <div class="flex items-center justify-between gap-2 mb-1">
                <span class="text-[11px] font-semibold text-brand-700">Ripple ID</span>
                <button onclick="copyText('${pkg.id}', 'Ripple ID ')" class="text-[11px] text-brand-700 hover:text-brand-900 font-semibold">复制</button>
              </div>
              <div class="text-sm font-mono font-semibold text-ink-900 truncate">${pkg.id}</div>
            </div>
          </div>
        </div>
        <div class="px-6 py-3 bg-ink-50 border-t border-ink-100 flex flex-wrap items-center justify-between gap-3 text-xs text-ink-500">
          <div class="flex items-center gap-2">
            ${Icon.users({ size: 12, color: "#94A3B8" })}
            <span>由 <strong class="text-ink-700">${creator}</strong> 创建</span>
            <span class="text-ink-300">·</span>
            <span class="num">${createdStr}</span>
          </div>
          <div class="text-ink-400">包体信息已写入当前游戏配置</div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-5 items-start">
        <div class="col-span-8 space-y-4">
          <div class="card overflow-hidden">
            <div class="px-5 py-4 border-b border-ink-100 bg-gradient-to-r from-brand-50 to-white">
              <div class="flex items-center justify-between gap-3">
                <div>
                  <div class="text-xs font-semibold tracking-wider text-brand-700 uppercase">Package Info</div>
                  <h2 class="text-base font-semibold text-ink-900 mt-0.5">包体基础信息</h2>
                </div>
                <span class="badge bg-brand-50 text-brand-700">包体级配置</span>
              </div>
            </div>
            <div class="p-5">
              <div class="rounded-xl border border-brand-200 bg-brand-50/70 p-4 mb-4">
                <div class="flex items-center justify-between gap-3 mb-2">
                  <div class="text-xs font-semibold text-brand-700">Ripple ID</div>
                  <button onclick="copyText('${pkg.id}', 'Ripple ID ')" class="flex items-center gap-1 text-xs text-brand-700 hover:text-brand-900 font-semibold">
                    ${Icon.copy({ size: 12, color: "#1E3A8A" })}
                    复制
                  </button>
                </div>
                <div class="font-mono text-lg font-semibold text-ink-900 break-all select-all">${pkg.id}</div>
                <p class="text-xs text-brand-900/75 leading-relaxed mt-2">SDK 系统为该游戏包体自动生成的唯一标识，用于后台定位、审计和后续配置引用。</p>
              </div>
              <div class="grid grid-cols-2 gap-3">
                ${packageInfoField("包体名称", packageDisplayName, false, "创建包体时录入的运营展示名称，便于运营和研发快速识别该包体。")}
                ${packageInfoField("Region", regionText, false, "该包体适用的发行地区；未选择地区时默认为全球。")}
                ${packageInfoField("Platform", pkg.platform, false, "该包体发布的应用平台，决定 App ID 字段类型和跳转规则。")}
                ${packageInfoField("Bundle ID / Package Name", pkg.bundle_id, true, "应用包名或 Bundle 标识，用于 SDK 初始化、平台匹配和包体识别。")}
                ${packageInfoField(appIdLabel, pkg.app_id, true, "应用商店或三方平台分配的应用 ID，用于商店跳转和归因识别。")}
                ${packageInfoField("所属游戏", `${game.name}（${game.code}）`, false, "包体归属的游戏项目，继承该游戏的全局凭证。")}
              </div>
            </div>
          </div>

          <div class="card overflow-hidden">
            <div class="px-5 py-4 border-b border-ink-100 flex items-center justify-between gap-3">
              <div>
                <div class="text-xs font-semibold tracking-wider text-ink-500 uppercase">Store Routing</div>
                <h2 class="text-base font-semibold text-ink-900 mt-0.5">应用商店链接</h2>
              </div>
              <button onclick="copyText('${pkg.store_url}', '应用商店链接 ')" class="flex items-center gap-1 text-xs text-brand-700 hover:text-brand-900 font-semibold">
                ${Icon.copy({ size: 12, color: "#1E3A8A" })}
                复制
              </button>
            </div>
            <div class="p-5">
              <div class="px-4 py-3 bg-ink-900 rounded-xl font-mono text-xs text-white break-all select-all shadow-inner">${pkg.store_url}</div>
              <p class="text-xs text-ink-500 leading-relaxed mt-2.5">用户点击分享链接后最终进入的应用商店或下载落地页地址。</p>
            </div>
          </div>

          <div class="card overflow-hidden border-l-4 border-l-brand-700">
            <div class="px-5 py-4 border-b border-ink-100 bg-white">
              <div class="flex items-center gap-2">
                <span class="text-xs font-semibold tracking-wider text-ink-500">继承游戏级凭证</span>
                <span class="badge bg-ok-50 text-ok-700">共享</span>
              </div>
            </div>
            <div class="p-5">
              <div class="grid grid-cols-2 gap-3">
                ${packageInfoField("Game ID（共享）", game.id, true, "所属游戏的全局唯一标识；同一游戏下所有包体共享同一个 Game ID。")}
                <div class="rounded-xl border border-warn-200 bg-warn-50 p-3">
                  <div class="text-xs font-semibold text-warn-800 mb-1.5">Game Secret（共享）</div>
                  <div class="text-sm font-semibold text-warn-900">继承所属游戏的 Game Secret</div>
                  <p class="text-[11px] text-warn-800/80 leading-relaxed mt-1.5">不为包体单独生成新密钥，避免同一游戏多包体凭证分裂。</p>
                </div>
              </div>
              <div class="mt-3 flex items-start gap-2 px-3 py-2.5 bg-brand-50 border border-brand-200 rounded-xl">
                <span class="w-5 h-5 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0 mt-0.5">${Icon.info({ size: 12, color: "#1E3A8A" })}</span>
                <span class="text-xs text-brand-900 leading-relaxed">同一款游戏下所有包体共用同一个 GameID 与 Game Secret；包体只保存 Region、Platform、Bundle ID、${appIdLabel} 和商店链接等包体级信息。</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-span-4 space-y-4">
          <div class="card p-5 sticky top-4">
            <div class="flex items-center gap-3 pb-4 border-b border-ink-100">
              <div class="w-10 h-10 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center">
                ${Icon.package({ size: 18, color: "#1E3A8A" })}
              </div>
              <div>
                <h3 class="text-sm font-semibold text-ink-900">接入摘要</h3>
                <p class="text-xs text-ink-500 mt-0.5">关键字段已准备完成</p>
              </div>
            </div>

            <div class="py-4 space-y-3">
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-ink-500">包体状态</span>
                <span class="badge bg-brand-50 text-brand-700">Draft</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-ink-500">Region</span>
                <span class="text-xs font-semibold text-ink-800 text-right">${pkg.region}</span>
              </div>
              <div class="flex items-center justify-between gap-3">
                <span class="text-xs text-ink-500">Platform</span>
                <span class="inline-flex items-center gap-1 text-xs font-semibold text-ink-800">${platformIcon}${pkg.platform}</span>
              </div>
              <div class="pt-3 border-t border-ink-100">
                <div class="text-xs text-ink-500 mb-1.5">包体名称</div>
                <div class="px-3 py-2 rounded-lg bg-ink-50 border border-ink-200 font-mono text-xs text-ink-800 break-all">${packageDisplayName}</div>
              </div>
            </div>

            <div class="pt-4 border-t border-ink-100">
              <div class="text-xs font-semibold tracking-wider text-ink-500 mb-3">下一步</div>
              <div class="space-y-2.5">
                ${packageNextStep("1", "接入 SDK", "使用 GameID 与包体 App ID 完成客户端初始化", "navigate('/dev/sdk-docs')")}
                ${packageNextStep("2", "查看配置历史", "在包体运营中查看该包体已关联的分享配置", `navigate('/ops/package-config/${pkg.id}/history')`)}
                ${packageNextStep("3", "查看包体", "回到游戏详情确认包体列表和配置状态", `navigate('/dev/games/${game.id}?tab=packages')`)}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card px-5 py-4 flex items-center justify-between gap-4">
        <div class="text-xs text-ink-500 leading-relaxed">
          你可以继续创建其它 Region × Platform 的包体，或进入游戏详情查看全部包体管理信息。
        </div>
        <div class="flex items-center justify-end gap-2 flex-shrink-0">
          ${UI.ghostBtn("继续创建包体", { onclick: `navigate('/dev/games/${game.id}/packages/create')` })}
          <button onclick="ackPackageResult('${pkg.id}', '${game.id}')" class="px-4 py-2 bg-brand-900 hover:bg-brand-800 text-white text-sm font-medium rounded-lg">
            完成并进入游戏详情
          </button>
        </div>
      </div>
    </div>
  `;
};

function packageAppIdLabel(platform) {
  if (/iOS/i.test(platform || "")) return "Apple App ID";
  if (/Google Play|Android/i.test(platform || "")) return "Google Play App ID";
  return "平台app Id";
}

function packageNextStep(index, title, desc, onclick) {
  return `
    <button onclick="${onclick}" class="w-full text-left rounded-xl border border-ink-200 bg-white p-3 hover:border-brand-300 hover:bg-brand-50/60 transition-colors group">
      <div class="flex items-start gap-2.5">
        <span class="w-6 h-6 rounded-full bg-brand-50 border border-brand-200 text-brand-700 text-xs font-bold flex items-center justify-center flex-shrink-0 group-hover:bg-brand-100">${index}</span>
        <span class="min-w-0">
          <span class="block text-sm font-semibold text-ink-900 group-hover:text-brand-700">${title}</span>
          <span class="block text-[11px] text-ink-500 leading-relaxed mt-0.5">${desc}</span>
        </span>
      </div>
    </button>
  `;
}

function packageInfoField(label, value, canCopy, desc = "") {
  const safeValue = value || "—";
  return `
    <div class="rounded-xl border border-ink-200 bg-white p-3 hover:border-ink-300 transition-colors">
      <div class="flex items-center justify-between gap-2 mb-1.5">
        <span class="text-xs font-semibold text-ink-500">${label}</span>
        ${canCopy ? `<button onclick="copyText('${safeValue}', '${label} ')" class="flex items-center gap-1 text-xs text-brand-700 hover:text-brand-900 font-semibold">${Icon.copy({ size: 12, color: "#1E3A8A" })}复制</button>` : ""}
      </div>
      <div class="px-3 py-2 bg-ink-50 border border-ink-100 rounded-lg font-mono text-xs text-ink-800 break-all select-all">${safeValue}</div>
      ${desc ? `<p class="text-[11px] text-ink-500 leading-relaxed mt-1.5">${desc}</p>` : ""}
    </div>
  `;
}

window.downloadPackageResult = function (packageId) {
  let result = window.__newPackageResult;
  if (!result || result.package?.id !== packageId) {
    const raw = sessionStorage.getItem(`easyshare:newPackageResult:${packageId}`);
    if (raw) {
      const parsed = JSON.parse(raw);
      result = { ...parsed, createdAt: new Date(parsed.createdAt) };
      window.__newPackageResult = result;
    }
  }
  const pkg = result?.package || MOCK.PACKAGES.find(p => p.id === packageId);
  const game = result?.game || MOCK.GAMES.find(g => g.id === pkg?.game_id);
  if (!pkg || !game) return;
  const appIdLabel = packageAppIdLabel(pkg.platform);
  const txt = `EasyShare 包体创建信息\n============================\nGame Name:   ${game.name}\nGame ID:     ${game.id}\nRipple ID:   ${pkg.id}\nPackage:     ${pkg.name}\nRegion:      ${pkg.region_name} (${pkg.region})\nPlatform:    ${pkg.platform}\nBundle ID:   ${pkg.bundle_id}\n${appIdLabel}: ${pkg.app_id}\nStore URL:   ${pkg.store_url}\nCreated At:  ${(result?.createdAt || new Date()).toISOString()}\n\n说明：该包体继承所属游戏的 GameID 与 Game Secret，不会生成独立 Game Secret。\n`;
  const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `easyshare-package-${pkg.id}.txt`;
  a.click();
  URL.revokeObjectURL(url);
  UI.toast("包体信息已下载", "success");
};

window.ackPackageResult = function (packageId, gameId) {
  window.__newPackageResult = null;
  sessionStorage.removeItem(`easyshare:newPackageResult:${packageId}`);
  navigate(`/dev/games/${gameId}?tab=packages`);
};

// __window_exports__
// Expose helpers for cross-page / inline onclick compatibility
;(window as any).packageAppIdLabel = packageAppIdLabel;
;(window as any).packageInfoField = packageInfoField;
;(window as any).packageNextStep = packageNextStep;
